/**
 * Single source of truth: Nominatim `address` → Google-style `address_components`.
 * - `county` is never typed as locality (avoids "South District"–style regions as city).
 * - State/region → administrative_area_level_1 only.
 * - Order of components in the array does not affect downstream parsing when using
 *   `pickCityAndCountryFromAddressComponents`.
 */

/** Bump when mapping rules change — geocodingService uses this in cache keys. */
export const NOMINATIM_ADDRESS_FORMAT_VERSION = 2;

const normKey = (s) => String(s || "").trim().toLowerCase();

const str = (v) => (v == null ? "" : String(v).trim());

const sameLabel = (a, b) => {
  const x = str(a);
  const y = str(b);
  return x && y && normKey(x) === normKey(y);
};

/**
 * @param {Record<string, unknown>} address - Nominatim `address` object
 * @returns {Array<{ long_name: string, short_name: string, types: string[] }>}
 */
export function buildAddressComponentsFromNominatimAddress(address) {
  if (!address || typeof address !== "object") {
    return [];
  }

  const locality = str(
    address.city || address.town || address.village || address.municipality || ""
  );

  let postalTown = str(address.city_district || address.borough || address.suburb || "");
  if (sameLabel(postalTown, locality)) {
    postalTown = "";
  }

  let county = str(address.county || "");
  if (sameLabel(county, locality)) {
    county = "";
  }

  const state = str(address.state || address.region || "");
  const country = str(address.country || "");
  const countryCode = str(address.country_code || "").toUpperCase();
  const postcode = str(address.postcode || "");

  const out = [];
  if (locality) {
    out.push({ long_name: locality, short_name: locality, types: ["locality"] });
  }
  if (postalTown) {
    out.push({ long_name: postalTown, short_name: postalTown, types: ["postal_town"] });
  }
  if (county) {
    out.push({
      long_name: county,
      short_name: county,
      types: ["administrative_area_level_2"],
    });
  }
  if (state) {
    out.push({
      long_name: state,
      short_name: state,
      types: ["administrative_area_level_1"],
    });
  }
  if (country) {
    out.push({
      long_name: country,
      short_name: countryCode || country,
      types: ["country"],
    });
  }
  if (postcode) {
    out.push({ long_name: postcode, short_name: postcode, types: ["postal_code"] });
  }
  return out;
}

/**
 * Order-independent: pick one display city + country from Google-format components.
 * Priority: locality → postal_town → administrative_area_level_2 → administrative_area_level_1
 * @param {Array<{ long_name?: string, types?: string[] }>|undefined|null} components
 * @returns {{ city?: string, country?: string }}
 */
export function pickCityAndCountryFromAddressComponents(components) {
  if (!Array.isArray(components)) {
    return {};
  }
  let locality;
  let postalTown;
  let admin2;
  let admin1;
  let countryName;
  for (const c of components) {
    const types = c.types || [];
    const name = typeof c.long_name === "string" ? c.long_name.trim() : "";
    if (!name) continue;
    if (types.includes("locality")) locality = name;
    if (types.includes("postal_town")) postalTown = name;
    if (types.includes("administrative_area_level_2")) admin2 = name;
    if (types.includes("administrative_area_level_1")) admin1 = name;
    if (types.includes("country")) countryName = name;
  }
  const city = locality || postalTown || admin2 || admin1;
  return { city, country: countryName };
}

/**
 * Forward-geocode Nominatim row → Google Places–compatible object.
 * @param {Record<string, unknown>} nominatimResult - one element from /search JSON
 * @returns {object|null}
 */
export function nominatimResultToGooglePlaceFormat(nominatimResult) {
  if (!nominatimResult) return null;
  const lat = parseFloat(nominatimResult.lat);
  const lon = parseFloat(nominatimResult.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

  const address = nominatimResult.address || {};
  const address_components = buildAddressComponentsFromNominatimAddress(address);

  return {
    address_components,
    formatted_address: nominatimResult.display_name || "",
    geometry: {
      location: {
        lat: () => lat,
        lng: () => lon,
      },
    },
    place_id: nominatimResult.place_id?.toString() || "",
    types: nominatimResult.type ? [nominatimResult.type] : [],
  };
}
